<template>
  <div
    v-if="$store.getters['session/currentUser']"
    :class="{active: active}"
    class="upsell-popout-component-container"
  >
    <div class="content">
      <div class="upsell">
        <div class="title">
          {{ title }}
        </div>
        <div class="list">
          <ul>
            <template v-if="userNeedsToRenew">
              <li>Do you like keeping your certifications current and never expiring?</li>
              <li>How about never having to take off work to go to a test center?</li>
              <li>Did you enjoy learning as you go with the remediation for each question?</li>
            </template>
            <template v-else>
              <li>Keep your certifications current – you’ll never expire if you keep answering questions.</li>
              <li>No need to take off work to go to a test center – earn your credits on any device, at your convenience.</li>
              <li>Want to know what you got right or wrong? Each question has remediation, so you learn as you go!</li>
            </template>
          </ul>
        </div>
      </div>
      <div class="price">
        $48 per year
      </div>
      <div class="price-explanation">
        {{ explanation }}
      </div>
      <div
        v-if="userNeedsToRenew"
        class="extra-explanation"
      >
        Keep my questions coming!
      </div>
      <a
        href="https://www.ase.com/subscribe"
        target="_blank"
        rel="noreferer noopener"
      >
        {{ buttonText }}
      </a>
      <div
        class="no-thanks"
        @click="toggle(false)"
      >
        <span>No Thanks</span>
      </div>
    </div>
  </div>
</template>

<script>
import Certification from '@/mixins/certifications';

export default {
  mixins: [Certification],
  data() {
    return {
      active: false,
    };
  },
  computed: {
    buttonText() {
      if (this.userNeedsToRenew) {
        return 'RENEW NOW';
      } else {
        return 'SUBSCRIBE';
      }
    },
    explanation() {
      if (this.userNeedsToRenew) {
        return 'Don’t stop now! Your subscription now includes A9! $48 per year includes all eligible Automobile Technician Certifications (A1-A9).';
      } else {
        return 'One payment includes all eligible Auto Technician Certifications, currently A1-A9.';
      }
    },
    lastPurchaseOrder() {
      return this.$store.getters['purchaseOrders/subscription'].slice(-1).pop() || {};
    },
    listener() {
      return this.$store.getters['ui/upsellPopoutStatus'];
    },
    title() {
      if (this.userNeedsToRenew) {
        return 'TIME TO RENEW YOUR SUBSCRIPTION!';
      } else {
        return 'ASE CERTIFICATION WITHOUT EXPIRATION';
      }
    },
  },
  created() {
    this.$watch('listener', (newValue, oldValue) => {
      if (newValue && !oldValue) {
        this.toggle({ show: true });
      }
    });
    this.$watch('$route', (newRoute, oldRoute) => {
      if (this.active) {
        if (newRoute !== oldRoute) {
          this.active = false;
        }
      }
    });
  },
  methods: {
    toggle(params) {
      this.active = params.show;
      this.$store.dispatch('ui/toggleUpsellPopout', { show: false });
    },
  },
};
</script>

<style lang='scss' scoped>
  .upsell-popout-component-container {
    background-color: transparent;
    bottom: 0;
    display: flex;
    left: 0;
    position: fixed;
    right: 0;
    top: rem-calc(48);
    transition: background-color 300ms 0ms, transform 0ms 300ms;
    transform: translate(100%, 0);
    z-index: 1000;

    &.active {
      background-color: rgba(0,0,0,.6);
      transform: none;
      transition: background-color 300ms 0ms, transform 0ms 0ms;

      @include breakpoint(small) {
        background-color: transparent;
      }

      .content {
        overflow-y: scroll;
        transform: none;
      }
    }

    @include breakpoint(small) {
      top: rem-calc(72);
    }

    .content {
      background: white url('../../assets/images/ase/upsell-background.svg') top no-repeat;
      background-size: cover;
      display: flex;
      flex-direction: column;
      margin-left: auto;
      max-width: 380px;
      padding: rem-calc(32);
      transition: transform 300ms ease-in-out;
      transform: translate(100%, 0);
      width: 30%;

      @include breakpoint(small) {
        padding: rem-calc(24);
      }

      > * {
        // for ie11 specifically
        flex-basis: auto !important;
        flex-shrink: 0 !important;
      }

      a {
        background: #ff671f;
        border-radius: rem-calc(2);
        color: white;
        cursor: pointer;
        display: block;
        font-size: rem-calc(24);
        font-weight: $bold;
        margin: rem-calc(12) auto rem-calc(4) auto;
        padding: rem-calc(12) rem-calc(24);
        text-align: center;
        width: rem-calc(200);
      }

      .price {
        color: #354052;
        font-size: rem-calc(24);
        font-weight: $bold;
        text-align: center;
      }

      .price-explanation {
        font-size: rem-calc(14);
        margin: rem-calc(8) 0;
        opacity: 0.8;
        text-align: center;
      }

      .extra-explanation {
        color: #ff671f;
        text-align: center;

        @include breakpoint(small) {
          display: none;
        }
      }

      .no-thanks {
        margin-top: rem-calc(12);
        text-align: center;

        span {
          border-bottom: 1px solid #354052;
          color: #354052;
          cursor: pointer;
          display: inline-block;
        }
      }

      @include breakpoint(small) {
        flex: 1;
        max-width: 100%;
      }

      .upsell {
        color: black;
        flex: 1;
        padding: rem-calc(32) 0 rem-calc(64) 0;

        @include breakpoint(medium) {
          padding: 0 0 rem-calc(32) 0;
        }

        .title {
          font-size: rem-calc(28);
          font-weight: $extra-bold;
          line-height: 1;
          margin-bottom: rem-calc(32);

          @include breakpoint(small) {
            margin-bottom: rem-calc(16);
          }
        }

        .list :deep(*) {
          > *:not(ul) {
            display: none;
          }

          ul {
            color: white;
            font-size: rem-calc(20);
            list-style-type: none;
            padding: 0;

            @include breakpoint(medium) {
              font-size: rem-calc(16);
            }

            li {
              line-height: 1.25;
              margin-bottom: rem-calc(8);

              &:before {
                content: '•';
                margin-right: rem-calc(6);
              }
            }
          }
        }
      }
    }
  }
</style>
